import React, { useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Grid, Card, CardContent,
  Breadcrumbs, Link, Chip, Divider, Avatar, Alert, Tabs, Tab, Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Edit as EditIcon,
  Message as MessageIcon, Person as PersonIcon, Email as EmailIcon,
  CalendarToday as CalendarTodayIcon, VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon, Chat as ChatIcon, Announcement as AnnouncementIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Conversation } from '../types/moderation';
import apiService from '../services/api';
import ConversationViewer from '../components/ConversationViewer';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user', id],
    queryFn: () => apiService.getUser(Number(id)),
    select: (response) => response.data,
    enabled: !!id,
  });

  const verifyUserMutation = useMutation({
    mutationFn: (userId: number) => apiService.verifyUser(userId),
    onSuccess: () => {
      setActionMessage({ type: 'success', text: 'Utilisateur marqué comme vérifié.' });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      let text = 'Échec de la vérification manuelle.';
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { error?: string } } }).response?.data;
        if (typeof data?.error === 'string' && data.error.length > 0) {
          text = data.error;
        }
      }
      setActionMessage({ type: 'error', text });
    },
  });

  const { data: conversationsData, isLoading: conversationsLoading } = useQuery({
    queryKey: ['user-conversations', id],
    queryFn: () => apiService.getUserConversations(Number(id), { limit: 50 }),
    select: (response) => response.data,
    enabled: !!id,
  });

  const { data: announcementsData, isLoading: announcementsLoading } = useQuery({
    queryKey: ['user-announcements', id],
    queryFn: () => apiService.getUserAnnouncements(Number(id), { limit: 50 }),
    select: (response) => response.data,
    enabled: !!id,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setTabValue(1); // Switch to conversations tab
  };

  const getRoleColor = (role: string) => {
    if (role === 'ROLE_SUPER_ADMIN') return 'secondary';
    if (role === 'ROLE_ADMIN') return 'info';
    if (role === 'ROLE_LOCATION_ADMIN') return 'primary';
    return 'default';
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'ROLE_SUPER_ADMIN': return 'Super Admin';
      case 'ROLE_ADMIN': return 'Admin';
      case 'ROLE_LOCATION_ADMIN': return 'Admin Partenaire';
      case 'ROLE_USER': return 'Utilisateur';
      default: return role;
    }
  };

  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (userError || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Impossible de charger les détails de l'utilisateur.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mt: 2 }}
        >
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/users')}
          sx={{ textDecoration: 'none' }}
        >
          Utilisateurs
        </Link>
        <Typography color="text.primary">
          {user.fullName || user.email}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src={user.avatar}
            sx={{ width: 64, height: 64, bgcolor: 'primary.light' }}
          >
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {user.fullName || 'Nom non renseigné'}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {user.email}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              {user.roles.map((role: string) => (
                <Chip
                  key={role}
                  label={getRoleLabel(role)}
                  size="small"
                  color={getRoleColor(role) as any}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {!user.isVerified ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<VerifiedUserIcon />}
              onClick={() => verifyUserMutation.mutate(user.id)}
              disabled={verifyUserMutation.isPending}
            >
              Vérifier manuellement
            </Button>
          ) : null}
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/users/${user.id}/edit`)}
          >
            Modifier
          </Button>
        </Box>
      </Box>

      {actionMessage ? (
        <Alert severity={actionMessage.type} sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage.text}
        </Alert>
      ) : null}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="user details tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<PersonIcon />}
            label="Informations"
            iconPosition="start"
          />
          <Tab
            icon={<ChatIcon />}
            label={`Conversations (${conversationsData?.conversations?.length || 0})`}
            iconPosition="start"
          />
          <Tab
            icon={<AnnouncementIcon />}
            label={`Annonces (${announcementsData?.announcements?.length || 0})`}
            iconPosition="start"
          />
        </Tabs>

        {/* User Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informations personnelles
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box display="flex" alignItems="center" mb={2}>
                    <EmailIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Nom complet
                      </Typography>
                      <Typography variant="body1">
                        {user.fullName || 'Non renseigné'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <CalendarTodayIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Date d'inscription
                      </Typography>
                      <Typography variant="body1">
                        {new Date(user.createdAt).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <SecurityIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Session
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={user.isOnline ? 'En ligne' : 'Hors ligne'}
                          size="small"
                          color={user.isOnline ? 'success' : 'default'}
                          variant={user.isOnline ? 'filled' : 'outlined'}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <CalendarTodayIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Dernière connexion
                      </Typography>
                      <Typography variant="body1">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {user.lastActivityAt ? (
                    <Box display="flex" alignItems="center" mb={2}>
                      <CalendarTodayIcon color="action" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Dernière activité
                        </Typography>
                        <Typography variant="body1">
                          {new Date(user.lastActivityAt).toLocaleString('fr-FR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Statut et permissions
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box display="flex" alignItems="center" mb={2}>
                    <VerifiedUserIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Statut
                      </Typography>
                      <Chip
                        label={
                          user.isActive
                            ? 'Actif'
                            : user.inactive_sensitive_content
                              ? 'Inactif (contenu sensible)'
                              : 'Inactif'
                        }
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <SecurityIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Vérification
                      </Typography>
                      <Chip
                        label={user.isVerified ? 'Vérifié' : 'Non vérifié'}
                        color={user.isVerified ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <MessageIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Conversations
                      </Typography>
                      <Typography variant="body1">
                        {conversationsData?.conversations?.length || 0} conversation(s)
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Conversations Tab */}
        <TabPanel value={tabValue} index={1}>
          {conversationsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedConversation ? (
            <ConversationViewer
              conversation={selectedConversation}
              onBack={() => setSelectedConversation(null)}
            />
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Conversations de {user.fullName || user.email}
              </Typography>
              
              {conversationsData?.conversations?.length === 0 ? (
                <Alert severity="info">
                  Cet utilisateur n'a aucune conversation.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {conversationsData?.conversations?.map((conversation: Conversation) => (
                    <Grid item xs={12} md={6} key={conversation.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => handleConversationSelect(conversation)}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {conversation.announcement.title}
                            </Typography>
                            <Chip
                              label={conversation.announcement.type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" mb={1}>
                            avec {conversation.other_user?.fullName || conversation.other_user?.email}
                          </Typography>
                          
                          {conversation.last_message && (
                            <Typography variant="body2" noWrap>
                              {conversation.last_message.content}
                            </Typography>
                          )}
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                            <Typography variant="caption" color="text.secondary">
                              {conversation.messages_count} message(s)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(conversation.updated_at).toLocaleDateString('fr-FR')}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </TabPanel>

        {/* Announcements Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Annonces de {user.fullName || user.email}
            </Typography>
            
            {announcementsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : announcementsData?.announcements?.length === 0 ? (
              <Alert severity="info">
                Cet utilisateur n'a publié aucune annonce.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {announcementsData?.announcements?.map((announcement: any) => (
                  <Grid item xs={12} md={6} key={announcement.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { 
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s ease-in-out'
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                          <Typography variant="h6" component="h3" noWrap sx={{ flexGrow: 1, mr: 1 }}>
                            {announcement.title}
                          </Typography>
                          <Chip
                            label={announcement.type}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {announcement.description}
                        </Typography>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {announcement.location?.city && announcement.location?.country 
                                ? `${announcement.location.city}, ${announcement.location.country}`
                                : 'Localisation non renseignée'
                              }
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(announcement.created_at).toLocaleDateString('fr-FR')}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                          <Chip
                            label={announcement.status === 'active' ? 'Actif' : announcement.status === 'inactive' ? 'Inactif' : announcement.status}
                            size="small"
                            color={announcement.status === 'active' ? 'success' : announcement.status === 'inactive' ? 'error' : 'default'}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {announcement.messages_count || 0} message(s)
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default UserDetailsPage;
